import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { PageComponent } from './page.component';
import { DataPreloaderService } from '../core/services/data-preloader.service';
import { GamePageGuard } from '../core/shared/guards/game-page.guard';
import { LangGuard } from '../core/shared/guards/lang.guard';
import { AuthGuard } from '../core/shared/guards/auth.guard';
import { CryptoGuard } from '../core/shared/guards/crypto.guard';
import { VipPageComponent } from './vip-page/vip-page.component';
import { CryptoAddressConfirmComponent } from './confirmation/crypto-address-confirm/crypto-address-confirm.component';

const confirmationRedirects: Routes = [
  {path: 'users/auth/telegram', redirectTo: 'social-login'},
  {path: ':lang/users/auth/telegram', redirectTo: 'social-login'},
  {path: 'users/auth/google_oauth2', redirectTo: 'social-login'},
  {path: ':lang/users/auth/google_oauth2', redirectTo: 'social-login'},
  {path: 'users/confirmation', redirectTo: 'confirmation/user'},
  {path: ':lang/users/confirmation', redirectTo: 'confirmation/user'},
  {path: 'users/password/edit', redirectTo: 'confirmation/password_edit'},
  {path: ':lang/users/password/edit', redirectTo: 'confirmation/password_edit'},
  {path: 'profile/limits/confirm', redirectTo: 'confirmation/limit'},
  {path: ':lang/profile/limits/confirm', redirectTo: 'confirmation/limit'},
  {path: 'profile/auth_providers/confirm', redirectTo: 'confirmation/auth_provider'},
  {path: ':lang/profile/auth_providers/confirm', redirectTo: 'confirmation/auth_provider'},
  {path: ':lang/profile/responsible-gaming', redirectTo: '/:lang'}, /* hide setup limits DEVSS-484 */
  {path: ':lang/home', redirectTo: '/'},
  {path: ':lang/vip', redirectTo: '/'},
  {path: 'users/unlock', redirectTo: 'confirmation/unlock'},
  {path: ':lang/users/unlock', redirectTo: 'confirmation/unlock'},
  {path: 'api/users/unlock', redirectTo: 'confirmation/unlock'},
  {path: ':lang/api/users/unlock', redirectTo: 'confirmation/unlock'},
  {path: 'deposit', redirectTo: 'profile/deposit'},
  {path: ':lang/deposit', redirectTo: 'profile/deposit'},
  {path: 'accounts/confirm_cashout_address/:paymentId/:token', component: CryptoAddressConfirmComponent},
  {path: ':lang/accounts/confirm_cashout_address/:paymentId/:token', component: CryptoAddressConfirmComponent},
];

const routes: Routes = [
  {
    path: '',
    component: PageComponent,
    resolve: {
      data: DataPreloaderService
    },
    children: [
      {path: '', loadChildren: () => import('./home/home.module').then(m => m.HomeModule), canActivate: [LangGuard], pathMatch: 'full'},
      {path: '404', loadChildren: () => import('./not-found/not-found.module').then(m => m.NotFoundModule)},
      {path: '403', loadChildren: () => import('./forbidden-page/forbidden-page.module').then(m => m.ForbiddenPageModule)},
      {path: 'login', redirectTo: '/?modal=login'},
      {path: 'payment-status', loadChildren: () => import('./payments-status/payments-status.module').then(m => m.PaymentsStatusModule), canActivate: [LangGuard]},
      {path: ':lang/payment-status', loadChildren: () => import('./payments-status/payments-status.module').then(m => m.PaymentsStatusModule), canActivate: [LangGuard, ]},
      {path: 'register', redirectTo: '/?modal=registration'},
      {path: 'transfer', loadChildren: () => import('./transfer/transfer.module').then(m => m.TransferModule)},
      ...confirmationRedirects,
      {path: ':lang', loadChildren: () => import('./home/home.module').then(m => m.HomeModule), canActivate: [LangGuard]},
      {path: ':lang/social-login', loadChildren: () => import('./social-handler/social-handler-page.module').then((m) => m.SocialHandlerPageModule), canActivate: [LangGuard]},
      {path: ':lang/profile', loadChildren: () => import('./account/account.module').then(m => m.AccountModule), canActivate: [LangGuard, AuthGuard, ]},
      {path: ':lang/confirmation', loadChildren: () => import('./confirmation/confirmation.module').then(m => m.ConfirmationModule), canActivate: [LangGuard, ]},
      {path: ':lang/loyalty-program', loadChildren: () => import('./loyalty/loyalty.module').then(m => m.LoyaltyModule), canActivate: [LangGuard, ]},
      {path: ':lang/promo', loadChildren: () => import('./promo/promo.module').then(m => m.PromoModule), canActivate: [LangGuard, ]},
      {path: ':lang/bonuses', loadChildren: () => import('./bonuses/bonuses.module').then(m => m.BonusesModule), canActivate: [LangGuard, ]},
      {path: ':lang/crypto', loadChildren: () => import('./crypto-register/crypto-register.module').then(m => m.CryptoRegisterModule), canActivate: [LangGuard, CryptoGuard, ]},
      {path: ':lang/lottery', loadChildren: () =>  import('./lottery/lottery.module').then(m => m.LotteryModule), canActivate: [LangGuard, ]},
      { path: ':lang/vip-program', component: VipPageComponent, canActivate: [LangGuard]},
      {path: ':lang/tournaments', loadChildren: () => import('./tournaments/tournaments.module').then(m => m.TournamentsModule), canActivate: [LangGuard, ]},
      {path: ':lang/about', loadChildren: () => import('./about-us/about-us.module').then(m => m.AboutUsModule), canActivate: [LangGuard, ]},
      {
        path: ':lang/crypto-spin',
        loadChildren: () => import('./crypto-spin/crypto-spin.module').then(m => m.CryptoSpinModule),
        canActivate: [LangGuard],
      },
      {path: ':lang/blog', loadChildren: () => import('./blog/blog.module').then(m => m.BlogModule), canActivate: [LangGuard, ]},
      {path: ':lang/mobile-app', loadChildren: () => import('./mobile-app/mobile-app.module').then(m => m.MobileAppModule), canActivate: [LangGuard, ]},
      {path: ':lang/games', loadChildren: () => import('./category-page/category-page.module').then(m => m.CategoryPageModule), canActivate: [LangGuard, ]},
      {path: ':lang/landing/:slug', loadChildren: () => import('./custom-category-page/custom-category-page.module').then(m => m.CustomCategoryPageModule), canActivate: [LangGuard, ]},
      {path: ':lang/bonus-shop', loadChildren: () => import('./bonus-shop/bonus-shop.module').then((m) => m.BonusShopModule), canActivate: [LangGuard], },
      {path: ':lang/jackpot/:slug', loadChildren: () => import('./jackpot/jackpot.module').then(m => m.JackpotModule), canActivate: [LangGuard]},
      {path: ':lang/play/:slug', loadChildren: () => import('./game-page/game-page.module').then(m => m.GamePageModule), canActivate: [GamePageGuard, LangGuard]},
      {path: ':lang/:slug', loadChildren: () => import('./static-page/static-page.module').then(m => m.StaticPageModule), canActivate: [LangGuard]},
      {path: ':lang/:type/:slug', loadChildren: () => import('./static-page/static-page.module').then(m => m.StaticPageModule), canActivate: [LangGuard]},
      {path: '**', redirectTo: '404'},
    ],
  }
];

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    RouterModule.forChild(routes)
  ],
  exports: [
    RouterModule
  ]
})
export class PageRoutingModule {
}
